/**
 * Códigos METRO ("todos os aeroportos" de uma cidade: SAO, RIO, PAR…) — o conceito
 * explícito que faltava ao domain (#2429). O voll-places emite esses códigos como
 * `airport:` comuns, mas eles NÃO existem nos mapas IATA→cidade (`airport_cities*`),
 * então todo consumidor os tratava como lixo do fuzzy. Este mapa os torna visíveis:
 * o ranker de candidatos escolhe o metro quando o cliente diz o nome da CIDADE, e o
 * combobox do planner os rotula de forma legível.
 *
 * Curadoria mínima: os metros do Brasil + os que o fornecedor emite nas rotas que um
 * viajante corporativo brasileiro digita. Duas invariantes, cobertas por teste:
 *   1. `city` usa a MESMA grafia pt-BR de `AIRPORT_CITIES` dos aeroportos servidos —
 *      é a comparação de igualdade que o ranker faz entre termo, metro e aeroporto;
 *   2. o código metro NÃO pode existir nos mapas IATA→cidade (senão `tierOf` o veria
 *      como aeroporto real e o conceito viraria ambíguo).
 *
 * `mainAirport` existe para o degrade de consumidores que NÃO aceitam metro (hoje o
 * price-calendar/SearchApi, que documenta só códigos de AEROPORTO): mapear o metro
 * pro principal aproxima em vez de quebrar. Nunca use `mainAirport` na busca de
 * ofertas — lá o metro é o valor correto (é o que alarga a comparação de tarifas).
 *
 * Pure (no IO): compile-time table lookups.
 */

export interface MetroArea {
  /** Nome pt-BR da cidade, grafia idêntica à de `AIRPORT_CITIES`. */
  city: string;
  /** Aeroporto principal — só para consumidores que não aceitam código metro. */
  mainAirport: string;
  /** Os aeroportos comerciais do metro, o principal primeiro: os atalhos "busque
   *  um aeroporto" quando a busca da cidade inteira estoura o prazo (#4465).
   *  Curados, não derivados de `cityByAirport`: Newark serve Nova York, mas o
   *  mapa de cidades o grava como "Newark". */
  airports: readonly string[];
}

export const METRO_AREAS: Record<string, MetroArea> = {
  SAO: { city: 'São Paulo', mainAirport: 'GRU', airports: ['GRU', 'CGH', 'VCP'] },
  RIO: { city: 'Rio de Janeiro', mainAirport: 'GIG', airports: ['GIG', 'SDU'] },
  BHZ: { city: 'Belo Horizonte', mainAirport: 'CNF', airports: ['CNF', 'PLU'] },
  BUE: { city: 'Buenos Aires', mainAirport: 'EZE', airports: ['EZE', 'AEP'] },
  NYC: { city: 'Nova York', mainAirport: 'JFK', airports: ['JFK', 'EWR', 'LGA'] },
  WAS: { city: 'Washington', mainAirport: 'IAD', airports: ['IAD', 'DCA', 'BWI'] },
  LON: { city: 'Londres', mainAirport: 'LHR', airports: ['LHR', 'LGW', 'LCY', 'STN', 'LTN'] },
  PAR: { city: 'Paris', mainAirport: 'CDG', airports: ['CDG', 'ORY'] },
  MIL: { city: 'Milão', mainAirport: 'MXP', airports: ['MXP', 'LIN'] },
};

function areaOf(iata: string | null | undefined): MetroArea | null {
  if (!iata) return null;
  return METRO_AREAS[iata.trim().toUpperCase()] ?? null;
}

/** O código é um metro ("todos os aeroportos")? Aeroporto real e desconhecido → false. */
export function isMetroCode(iata: string | null | undefined): boolean {
  return areaOf(iata) !== null;
}

/** Cidade pt-BR do metro (`"SAO"` → `"São Paulo"`); não-metro → `null`. */
export function metroCityOf(iata: string | null | undefined): string | null {
  return areaOf(iata)?.city ?? null;
}

/** Aeroporto principal do metro (`"SAO"` → `"GRU"`); não-metro → `null`. */
export function metroMainAirportOf(iata: string | null | undefined): string | null {
  return areaOf(iata)?.mainAirport ?? null;
}

/** Os aeroportos do metro (`"NYC"` → `["JFK", "EWR", "LGA"]`), o principal
 *  primeiro; não-metro → `[]`. */
export function metroAirportsOf(iata: string | null | undefined): readonly string[] {
  return areaOf(iata)?.airports ?? [];
}
